import { useEffect, useRef, useCallback } from 'react'

declare global {
  interface Window {
    turnstile: {
      render: (container: HTMLElement, options: TurnstileOptions) => string
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
    onTurnstileLoad?: () => void
  }
}

interface TurnstileOptions {
  sitekey: string
  callback: (token: string) => void
  'error-callback'?: () => void
  'expired-callback'?: () => void
  theme?: 'light' | 'dark' | 'auto'
  size?: 'normal' | 'compact' | 'invisible'
  appearance?: 'always' | 'execute' | 'interaction-only'
}

interface CloudflareTurnstileProps {
  onVerify: (token: string) => void
  onError?: () => void
  onExpire?: () => void
  theme?: 'light' | 'dark' | 'auto'
  size?: 'normal' | 'compact' | 'invisible'
}

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || ''

const CloudflareTurnstile = ({
  onVerify,
  onError,
  onExpire,
  theme = 'auto',
  size = 'normal'
}: CloudflareTurnstileProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)

  const renderWidget = useCallback(() => {
    if (!containerRef.current || !window.turnstile || widgetIdRef.current) return

    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      callback: onVerify,
      'error-callback': onError,
      'expired-callback': onExpire,
      theme,
      size
    })
  }, [onVerify, onError, onExpire, theme, size])

  useEffect(() => {
    // Check if script is already loaded
    const existingScript = document.querySelector('script[src*="turnstile"]')

    if (existingScript) {
      // Script already exists, check if turnstile is ready
      if (window.turnstile) {
        renderWidget()
      } else {
        // Wait for it to load
        window.onTurnstileLoad = renderWidget
      }
      return
    }

    // Load the Turnstile script
    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad'
    script.async = true
    script.defer = true

    window.onTurnstileLoad = renderWidget

    document.head.appendChild(script)

    return () => {
      // Cleanup widget on unmount
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
    }
  }, [renderWidget])

  // Don't render anything if no site key is configured
  if (!TURNSTILE_SITE_KEY) {
    console.warn('Cloudflare Turnstile: REACT_APP_TURNSTILE_SITE_KEY is not configured')
    return null
  }

  return <div ref={containerRef} style={{ marginTop: '16px', marginBottom: '8px' }} />
}

export default CloudflareTurnstile
